import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { requestLoginOtp } from '@/api/auth';
import { useAuth } from '@/store/auth';
import { NightBackground, setConstellationReveal } from './NightBackground';

// Holding the logo reveals the constellations for as long as it's held, the
// same as holding 'r'. A quick click would otherwise flash for milliseconds,
// so the reveal always lingers at least this long before fading back.
const MIN_REVEAL_MS = 1600;

export default function LoginPage() {
  const navigate = useNavigate();
  const setPendingPhone = useAuth((s) => s.setPendingPhone);
  // India-only login. Backend stores country_code without the leading "+",
  // so send raw digits.
  const countryCode = '91';
  const [mobile, setMobile] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const revealStartedAt = useRef(0);
  const revealTimer = useRef<number | undefined>(undefined);

  function startReveal() {
    window.clearTimeout(revealTimer.current);
    revealStartedAt.current = performance.now();
    setConstellationReveal(true);
  }

  function endReveal() {
    const held = performance.now() - revealStartedAt.current;
    window.clearTimeout(revealTimer.current);
    revealTimer.current = window.setTimeout(
      () => setConstellationReveal(false),
      Math.max(0, MIN_REVEAL_MS - held),
    );
  }

  // Leaving the page mid-reveal would otherwise strand the pending timeout.
  useEffect(
    () => () => {
      window.clearTimeout(revealTimer.current);
      setConstellationReveal(false);
    },
    [],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mobile.trim()) {
      toast.error('Please enter your mobile number');
      return;
    }
    if (!accepted) {
      toast.error('Please accept the Terms & Privacy Policy to continue');
      return;
    }
    // Indian numbers must be 10 digits starting 6–9; reject malformed input
    // up front with a clear message instead of the backend's
    // "Number Not Registered" (TC006/TC008).
    const valid =
      countryCode === '91' ? /^[6-9]\d{9}$/.test(mobile) : /^\d{6,15}$/.test(mobile);
    if (!valid) {
      toast.error('Invalid phone number');
      return;
    }
    setSubmitting(true);
    try {
      await requestLoginOtp({ country_code: countryCode, mobile_no: mobile.trim() });
      setPendingPhone({ country_code: countryCode, mobile_no: mobile.trim() });
      toast.success('OTP sent');
      navigate('/login/otp');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <NightBackground />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 lg:justify-end lg:pr-16">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center lg:text-left">
            <button
              type="button"
              onPointerDown={startReveal}
              onPointerUp={endReveal}
              onPointerLeave={endReveal}
              onPointerCancel={endReveal}
              // Keyboard activation fires click without pointer events, so the
              // press and release are driven off the key itself.
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) startReveal();
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter' || e.key === ' ') endReveal();
              }}
              aria-label="Reveal the constellations"
              title="Reveal the constellations"
              className="mx-auto mb-4 block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 lg:mx-0"
            >
              <img
                src="/icons/partapp-icon.png"
                alt="PartApp"
                width={56}
                height={56}
                className="rounded-xl shadow-lg"
                draggable={false}
              />
            </button>
            <h1 className="text-3xl font-bold text-white">Welcome to PartApp</h1>
            <p className="mt-2 text-sm text-white/80">
              Sign in with your registered mobile number.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-4 rounded-2xl border border-white/10 bg-white/95 p-6 shadow-2xl backdrop-blur-md dark:bg-zinc-900/95"
          >
            <div className="flex gap-2">
              <div
                className="input-base flex h-10 w-[4.5rem] shrink-0 items-center justify-center gap-1 px-0 text-base sm:text-sm"
                aria-label="Country code: India +91"
              >
                <span aria-hidden>🇮🇳</span>
                <span>+91</span>
              </div>
              <Input
                name="mobile"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Mobile number"
                maxLength={10}
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
              />
            </div>

            <label className="flex items-start gap-2 text-xs text-app-hint">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                aria-label="Accept Terms & Conditions and Privacy Policy"
              />
              <span>
                I agree to PartApp's{' '}
                <a
                  href="https://www.partapp.in/terms-and-conditions.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline underline-offset-2"
                >
                  Terms &amp; Conditions
                </a>{' '}
                and{' '}
                <a
                  href="https://www.partapp.in/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline underline-offset-2"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>

            <Button
              type="submit"
              loading={submitting}
              disabled={!accepted}
              fullWidth
              size="lg"
            >
              Send OTP
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
